// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfficerDetailFacade } from '@intrensic/shared/officer-details';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NotificationService } from '@shared/feature/notification-handling';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CaseDetailsApiService } from '../api/case-api.service';
import * as CaseDetailsActions from './data-access.actions';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';
import { CaseDetailsResponse } from '../models/case-response.models';
import { AgencyBase, CaseDetails, CaseEvidence, CaseFolder, CaseOfficerBase, Tag, UidModel } from '../models/case.models';
import { CadCategory, CategoryBase } from '@shared/utils/category';
import { AddCaseMemberRequest, InviteCaseGuestsRequest, RemoveCaseMemberRequest, RemoveCaseEvidenceRequest, MoveEvidenceFileRequest } from '../models/case-request.models';
import { MemberType } from '../models/enums';
import { UserRole } from '@shared/utils/base-auth';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionType, CaseDetailsActionResult } from '../models/case-action-result.models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ModalService } from '@shared/feature/modal';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TranslationFromJsonService } from '@shared/feature/translate';

@Injectable()
export class CaseDetailsEffects {
  fetchCaseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.fetchCaseDetails),
      switchMap((payload) =>
        this.caseDetailsApiService
          .getCaseByUid(payload.uid)
          .pipe(switchMap((response) => [CaseDetailsActions.fetchCaseDetailsSuccess({ caseDetails: this.mapResponseToViewModel(response) })]))
      )
    )
  );

  fetchCaseEvidenceFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.fetchCaseEvidenceFiles),
      switchMap(() =>
        this.caseDetailsApiService.getCaseEvidenceFiles().pipe(
          switchMap((evidenceFilesResponse) => [
            CaseDetailsActions.fetchCaseEvidenceFilesSuccess({
              evidenceFiles: evidenceFilesResponse.map(
                (x) =>
                  new CaseEvidence(
                    x.UID,
                    new UidModel(x.Case.UID),
                    x.CaseFolder !== null ? new UidModel(x.CaseFolder.UID) : null,
                    x.OriginalFileDate,
                    x.CreatedOn,
                    x.Title,
                    x.OrganisationalID,
                    x.OrganisationalIdCad,
                    x.FileType,
                    x.Status,
                    new CaseOfficerBase(x.User.UID, x.User.FirstName, x.User.LastName, x.User.EmailAddress, x.User.PictureFullPath),
                    x.ThumbnailFullPath,
                    x.IsLocked
                  )
              ),
            }),
          ])
        )
      )
    )
  );

  fetchCaseEvidenceFolders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.fetchCaseEvidenceFolders),
      switchMap(() =>
        this.caseDetailsApiService
          .getCaseEvidenceFolders()
          .pipe(
            switchMap((evidenceFoldersResponse) => [
              CaseDetailsActions.fetchCaseEvidenceFoldersSuccess({ evidenceFolders: evidenceFoldersResponse.map((x) => new CaseFolder(x.CreatedOn, x.Name, x.UID)) }),
            ])
          )
      )
    )
  );

  fetchCaseMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.fetchCaseMembers),
      switchMap(() =>
        this.caseDetailsApiService.getCaseMembers().pipe(switchMap((caseMembersResponse) => [CaseDetailsActions.fetchCaseMembersSuccess({ caseMembers: caseMembersResponse })]))
      )
    )
  );

  fetchCaseDetailsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.fetchCaseDetailsSuccess),
      switchMap(() => {
        return [CaseDetailsActions.fetchCaseEvidenceFolders(), CaseDetailsActions.fetchCaseEvidenceFiles(), CaseDetailsActions.fetchCaseMembers()];
      })
    )
  );

  updateCaseDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.updateCase),
      exhaustMap((action) =>
        this.caseDetailsApiService.updateCase(action.updatedCaseDetails).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonString.instant('changes-saved'));
            this.modalService.closeAllModals();
          }),
          map((updatedCase) => CaseDetailsActions.updateCaseSuccess({ updatedCaseDetails: this.mapResponseToViewModel(updatedCase) })),
          catchError((error: HttpErrorResponse) =>
            of(
              CaseDetailsActions.updateCaseDetailsActionResult({
                actionResult: new CaseDetailsActionResult(ActionType.UpdateCaseDetails, error, false),
              })
            )
          )
        )
      )
    )
  );

  addCaseMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.addCaseMember),
      exhaustMap((action) =>
        this.caseDetailsApiService.addMemberToCase(new AddCaseMemberRequest(action.member.role, action.member.uid)).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('user-added-to-folder'))),
          map(() => CaseDetailsActions.addCaseMemberSuccess({ member: action.member })),
          catchError((error: HttpErrorResponse) => {
            this.notificationService.addError(error.error.Message ?? this.translationFromJsonString.instant('error-occured'));

            return of(CaseDetailsActions.addCaseMemberFailure());
          })
        )
      )
    )
  );

  inviteGuests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.inviteGuests),
      exhaustMap((action) =>
        this.caseDetailsApiService.inviteGuestsToCase(action.request).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('invitation-sent'))),
          map(() => CaseDetailsActions.fetchCaseMembers())
        )
      )
    )
  );

  updateCaseMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.updateCaseMember),
      exhaustMap((action) => {
        if (action.member.memberType === MemberType.User) {
          return this.caseDetailsApiService.addMemberToCase(new AddCaseMemberRequest(action.role, action.member.uid)).pipe(
            tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('permission-changed'))),
            map(() => CaseDetailsActions.updateCaseMemberSuccess({ member: action.member, role: action.role }))
          );
        }

        return this.caseDetailsApiService.inviteGuestsToCase(new InviteCaseGuestsRequest([action.member.emailAddress], action.role, '')).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('permission-changed'))),
          map(() => CaseDetailsActions.updateCaseMemberSuccess({ member: action.member, role: action.role }))
        );
      })
    )
  );

  updateCaseMemberSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.updateCaseMemberSuccess),
      exhaustMap((action) =>
        this.officerDetailFacade.getLoggedInOfficer().pipe(
          map((officer) => {
            if (officer.uid === action.member.uid && officer.role !== UserRole.SuperAdmin && officer.role !== UserRole.Supervisor) {
              return CaseDetailsActions.updateOfficerCaseRole({ role: action.role });
            }

            return CaseDetailsActions.updateOfficerCaseRole({ role: null });
          })
        )
      )
    )
  );

  removeCaseMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.removeMember),
      exhaustMap((action) =>
        this.caseDetailsApiService.removeMemberFromCase(new RemoveCaseMemberRequest(action.memberUid)).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('user-removed-from-folder'))),
          map(() => CaseDetailsActions.removeMemberSuccess({ memberUid: action.memberUid }))
        )
      )
    )
  );

  removeEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.removeEvidence),
      exhaustMap((action) =>
        this.caseDetailsApiService.removeCaseEvidence(new RemoveCaseEvidenceRequest(action.evidenceUid)).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('file-removed-from-folder'))),
          map(() => CaseDetailsActions.removeEvidenceSuccess({ evidenceUid: action.evidenceUid })),
          catchError(() => of(CaseDetailsActions.removeEvidenceFailure()))
        )
      )
    )
  );

  addFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.addFolder),
      exhaustMap((payload) =>
        this.caseDetailsApiService.createNewFolder(payload.folderRequest).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonString.instant('folder-created'));
            this.modalService.closeAllModals();
          }),
          map((uid) => CaseDetailsActions.addFolderSuccess({ folder: new CaseFolder(new Date(), payload.folderRequest.Name, uid) })),
          catchError((error: HttpErrorResponse) =>
            of(
              CaseDetailsActions.updateCaseDetailsActionResult({
                actionResult: new CaseDetailsActionResult(ActionType.AddFolder, error, false),
              })
            )
          )
        )
      )
    )
  );

  addEvidenceFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.addEvidenceFile),
      exhaustMap((payload) =>
        this.caseDetailsApiService.addEvidenceToCase(payload.evidenceRequest).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('file-added-to-folder'))),
          map(() => CaseDetailsActions.addEvidenceFileSuccess({ evidence: payload.evidenceDetails }))
        )
      )
    )
  );

  moveEvidenceFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.moveEvidenceFileToFolder),
      exhaustMap((payload) =>
        this.caseDetailsApiService
          .moveEvidenceFile(new MoveEvidenceFileRequest(payload.destinationFolder ? payload.destinationFolder.uid : payload.destinationFolder, [payload.movedFileUid]))
          .pipe(
            tap(() =>
              this.notificationService.addSuccess(
                payload.destinationFolder
                  ? this.translationFromJsonString.instant('file-is-moved-to-sub-folder')
                  : this.translationFromJsonString.instant('file-is-moved-back-to-parent-folder')
              )
            ),
            map(() => CaseDetailsActions.moveEvidenceFileToFolderSuccess({ movedFileUid: payload.movedFileUid, destinationFolder: payload.destinationFolder }))
          )
      )
    )
  );

  deleteFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseDetailsActions.deleteFolder),
      exhaustMap((payload) =>
        this.caseDetailsApiService.deleteFolder(payload.folderUid).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonString.instant('folder-deleted'))),
          map(() => CaseDetailsActions.deleteFolderSuccess({ folderUid: payload.folderUid }))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private caseDetailsApiService: CaseDetailsApiService,
    private notificationService: NotificationService,
    private officerDetailFacade: OfficerDetailFacade,
    private modalService: ModalService,
    private translationFromJsonString: TranslationFromJsonService
  ) {}

  private mapResponseToViewModel(response: CaseDetailsResponse): CaseDetails {
    return new CaseDetails(
      response.UID,
      response.Name,
      response.OrganisationalID,
      response.Status,
      new AgencyBase(response.Customer.Name, response.Customer.ShortName, response.Customer.UID),
      response.UpdatedOn,
      response.CreatedOn,
      new CaseOfficerBase(response.User.UID, response.User.FirstName, response.User.LastName, response.User.EmailAddress, response.User.PictureFullPath),
      response.UserCaseRole,
      response.Description,
      response.Categories.map(
        (x) =>
          new CategoryBase(
            x.Name,
            x.RetainDays,
            x.UseRetainDays,
            x.IsArchived,
            x.IsDefault,
            x.CadCategories.map((y) => new CadCategory(y.Name, y.Uid)),
            x.AppliedOnFiles,
            x.UID
          )
      ),
      response.Tags.map((x) => new Tag(x.Value, x.UID))
    );
  }
}
